import {
   PipelineItemAssociatedPipeline,
   PipelineItemPipelines
} from '@flowwai-frontend/bps-officer-web/graphql'
import { getErrorMessageFromErrorType } from '@flowwai-frontend/shared/utils/common'

import { useRecordWisePipelinesStore } from '../../hooks/stores/useRecordWisePipelinesStore'
import { PipelineItemPipelinesType } from '../../types'
import { GetPipelinesForPipelineItemsQuery as Query } from './query.generatedTypes'

interface ResponseArgsType {
   data: Query | undefined

   onHandleError: (
      errorMessage: string,
      data: Query['getPipelinesForPipelineItems']
   ) => void

   onSuccess?: (pipelineIds: Array<string>) => void
}

interface ReturnType {
   handleAPIResponse: (args: ResponseArgsType) => void
}

const useAPIResponseHandler = (): ReturnType => {
   const recordWisePipelinesStore = useRecordWisePipelinesStore()

   const getPipelinesForPipelineItem = (
      pipelineItemPipelines: PipelineItemPipelines
   ): void => {
      const pipelineIds: string[] = pipelineItemPipelines.pipelines.reduce(
         (
            totalPipelineIds: string[],
            pipelineData: PipelineItemAssociatedPipeline
         ) => {
            const { pipeline, hasPermission } = pipelineData

            return hasPermission
               ? [...totalPipelineIds, pipeline.pipelineId]
               : totalPipelineIds
         },
         []
      )

      recordWisePipelinesStore.setRecordPipelineIds(
         pipelineItemPipelines.pipelineItemId,
         pipelineIds
      )
   }

   const handleAPIResponse = (
      args: ResponseArgsType
   ): PipelineItemPipelinesType[] => {
      if (!args.data) return [] as Array<PipelineItemPipelinesType>

      const { getPipelinesForPipelineItems } = args.data

      switch (getPipelinesForPipelineItems.__typename) {
         case 'PipelinesForPipelineItemsResult': {
            getPipelinesForPipelineItems.pipelineItemWisePipelines.forEach(
               eachItem =>
                  getPipelinesForPipelineItem(eachItem as PipelineItemPipelines)
            )

            const pipelineIds: Array<string> = []

            getPipelinesForPipelineItems.pipelineItemWisePipelines.forEach(
               pipelineData =>
                  pipelineData.pipelines.forEach(pipeline => {
                     if (
                        pipeline.hasPermission &&
                        !pipelineIds.includes(pipeline.pipeline.pipelineId)
                     ) {
                        pipelineIds.push(pipeline.pipeline.pipelineId)
                     }
                  })
            )

            args.onSuccess?.(pipelineIds)
            break
         }

         case 'PipelineItemsAccessDenied':
         case 'PipelineItemsAreDeleted':
         case 'PipelineItemsNotFound':
         default: {
            const errorMessage = getErrorMessageFromErrorType(
               getPipelinesForPipelineItems.__typename
            )
            args.onHandleError?.(errorMessage, getPipelinesForPipelineItems)
         }
      }

      return []
   }

   return {
      handleAPIResponse
   }
}

export default useAPIResponseHandler
